/**
 * MobileComplianceFindings — per-portfolio compliance findings drill-down.
 *
 * CLUSTER-3 (research-visibility uplift). The traffic-light surface (MobileComplianceTrafficLight) shows
 * one RAG card per portfolio but is a DEAD END — there is no way to see WHICH findings drive the colour.
 * This screen, reached by tapping a card, lists the OPEN findings for one portfolio worst-first, each with
 * a severity badge + age, and lets the user "Mark acknowledged" inline.
 *
 * Route: /partner/m/compliance/:portfolio
 *
 * DATA — two SECURITY DEFINER RPCs, both gated identically to the traffic light
 * (has_surface_access cio|pm|analyst, raising 42501 otherwise):
 *   • mobile_compliance_findings(_portfolio) → JSON ARRAY [{ id, title, severity, status, created_at }, …]
 *     (OPEN = status in detected|contained; LIMIT 100; severity desc, created_at desc).
 *   • mobile_acknowledge_finding(_finding_id) → { ok, reason, current } — maps detected → contained
 *     (breach_status has no 'acknowledged' label). VOLATILE write; a LIVE action, NOT queued offline.
 *
 * READ side is offline-aware, mirroring the sibling mobile pages exactly:
 *   • ONE useCachedQuery (cacheKey scoped per portfolio) write-throughs the resolved array; the queryFn
 *     THROWS on { error } so the cached last-known-good keeps serving offline (and so the 42501 no-access
 *     state can be surfaced when there's nothing cached).
 *   • A stable EMPTY array guarantees the screen is never blank on a cold offline start.
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *
 * ACKNOWLEDGE is a live write: it's disabled offline (clear hint), shows a per-row spinner, optimistically
 * drops the acknowledged row, refetches in the background, and surfaces a toast. Generated supabase types
 * are stale for these RPCs → cast through `any` (matching the other cluster screens). Web/desktop untouched.
 */
import { useCallback, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ChevronLeft, ShieldAlert, ShieldCheck, Check, Loader2, WifiOff } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shapes — exactly the keys mobile_compliance_findings() builds.
// ---------------------------------------------------------------------------

type Severity = 'critical' | 'high' | 'medium' | 'low';

interface FindingRow {
  id: string;
  title: string | null;
  severity: Severity | string | null;
  status: string | null;
  created_at: string | null;
}

const EMPTY_FINDINGS: FindingRow[] = [];

// Postgres SQLSTATE for insufficient_privilege — the RPC raises this without cio/pm/analyst surface access.
const NO_ACCESS_CODE = '42501';

function errorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

async function fetchFindings(portfolio: string): Promise<FindingRow[]> {
  const { data, error } = await (supabase.rpc as any)('mobile_compliance_findings', {
    _portfolio: portfolio,
  });
  // MUST throw so useCachedQuery keeps the cached placeholder offline and the 42501 state can surface.
  if (error) throw error;
  return Array.isArray(data) ? (data as FindingRow[]) : [];
}

// mobile_acknowledge_finding envelope (types stale → described locally).
interface AckResult {
  ok?: boolean;
  reason?: 'acknowledged' | 'already_applied' | 'not_found' | 'stale' | string;
  current?: { status?: string | null } & Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Severity styling — colour + icon + text (never colour alone), light + dark correct.
// ---------------------------------------------------------------------------

const SEVERITY_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };

interface SeverityStyle {
  label: string;
  badge: string;
  dot: string;
}

const SEVERITY_STYLES: Record<Severity, SeverityStyle> = {
  critical: {
    label: 'Critical',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    dot: 'bg-red-500',
  },
  high: {
    label: 'High',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    dot: 'bg-red-500',
  },
  medium: {
    label: 'Medium',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  low: {
    label: 'Low',
    badge: 'bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground/50',
  },
};

function severityStyle(sev: string | null | undefined): SeverityStyle {
  const key = (sev ?? 'low').toLowerCase();
  return SEVERITY_STYLES[(key as Severity)] ?? SEVERITY_STYLES.low;
}

function statusLabel(status: string | null): string {
  switch ((status ?? '').toLowerCase()) {
    case 'detected':
      return 'Detected';
    case 'contained':
      return 'Contained';
    case 'remediated':
      return 'Remediated';
    case 'reported':
      return 'Reported';
    default:
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Open';
  }
}

function relativeAge(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileComplianceFindings() {
  const online = useIsOnline();
  const { toast } = useToast();
  const { portfolio = '' } = useParams<{ portfolio: string }>();

  const feed = useCachedQuery<FindingRow[]>({
    queryKey: ['mobile-compliance-findings', portfolio],
    cacheKey: `mobile.compliance_findings.${portfolio}`,
    queryFn: () => fetchFindings(portfolio),
    enabled: portfolio.length > 0,
  });

  // Locally-acknowledged ids (optimistically dropped once the RPC lands) so the row disappears without
  // waiting for the refetch.
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(() => new Set());
  // Per-row busy state — guards double-submit while an acknowledge is in flight.
  const [busyId, setBusyId] = useState<string | null>(null);

  const acknowledge = useCallback(
    async (id: string) => {
      if (busyId || !online) return;
      setBusyId(id);
      try {
        const { data, error } = await (supabase.rpc as any)('mobile_acknowledge_finding', {
          _finding_id: id,
        });
        if (error) {
          const denied = errorCode(error) === NO_ACCESS_CODE;
          toast({
            variant: 'destructive',
            title: denied ? 'Not allowed' : 'Couldn’t update',
            description: denied
              ? 'You don’t have permission to action this finding.'
              : 'Something went wrong — please try again.',
          });
          return;
        }
        const res = (data ?? {}) as AckResult;
        // ok covers both a fresh acknowledge and an already-applied (someone else contained it first).
        if (res.ok) {
          setAcknowledgedIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
          });
          toast({
            title: res.reason === 'already_applied' ? 'Already actioned' : 'Acknowledged',
            description:
              res.reason === 'already_applied'
                ? 'This finding was already handled.'
                : 'Marked as contained.',
          });
          void feed.refetch();
          return;
        }
        if (res.reason === 'not_found') {
          setAcknowledgedIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
          });
          toast({ title: 'No longer available', description: 'This finding has gone.' });
          void feed.refetch();
          return;
        }
        toast({
          variant: 'destructive',
          title: 'Couldn’t update',
          description: 'Unexpected response — please try again.',
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Couldn’t reach the server',
          description: 'Check your connection and try again.',
        });
      } finally {
        setBusyId(null);
      }
    },
    [busyId, online, toast, feed],
  );

  const rows = (feed.data ?? EMPTY_FINDINGS).filter((f) => !acknowledgedIds.has(f.id));

  // Worst-first safety net (the RPC already orders severity desc, but the JSON order is belt-and-braces).
  const sorted = [...rows].sort((a, b) => {
    const bySev =
      (SEVERITY_RANK[(b.severity ?? 'low').toString().toLowerCase()] ?? 0) -
      (SEVERITY_RANK[(a.severity ?? 'low').toString().toLowerCase()] ?? 0);
    if (bySev !== 0) return bySev;
    return (b.created_at ?? '').localeCompare(a.created_at ?? '');
  });

  const noAccess = errorCode(feed.error) === NO_ACCESS_CODE;

  const showStale = feed.isFromCache && feed.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && feed.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(feed.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  // No cached data AND access denied → graceful no-access state (don't blank).
  const showNoAccess = noAccess && rows.length === 0;
  // No cached data, no error, finished loading, empty payload → "all clear" empty state.
  const showEmpty = !showNoAccess && rows.length === 0 && !feed.isLoading;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/compliance"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Traffic lights
        </Link>
        <p className="text-sm text-muted-foreground">Compliance findings</p>
        <h1 className="font-heading text-2xl font-semibold text-primary">{portfolio || 'Portfolio'}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {sorted.length > 0 && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              {sorted.length} open finding{sorted.length === 1 ? '' : 's'}
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      {showNoAccess && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-heading text-sm font-semibold">No access</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            You don&apos;t have access to compliance findings. This view is available to CIO,
            portfolio-manager and analyst roles.
          </p>
        </div>
      )}

      {showEmpty && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-500/10 text-green-600 dark:text-green-400">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </span>
            <h2 className="font-heading text-sm font-semibold">All clear</h2>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            There are no open findings for {portfolio || 'this portfolio'} right now.
          </p>
          {!online && (
            <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
              You’re offline — this will refresh once you reconnect.
            </p>
          )}
        </div>
      )}

      {!online && sorted.length > 0 && (
        <p className="mb-3 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-amber-600">
          <WifiOff className="h-3 w-3" aria-hidden="true" />
          Offline — acknowledging needs a connection
        </p>
      )}

      <div className="space-y-3">
        {sorted.map((f) => {
          const style = severityStyle(f.severity);
          const age = relativeAge(f.created_at);
          const busy = busyId === f.id;
          return (
            <section
              key={f.id}
              className="rounded-xl border border-border bg-card p-4"
              aria-label={`${style.label} finding: ${f.title ?? 'Untitled finding'}`}
            >
              <div className="flex items-start gap-2">
                <ShieldAlert
                  className={cn(
                    'mt-0.5 h-4 w-4 shrink-0',
                    style.dot === 'bg-red-500'
                      ? 'text-red-600 dark:text-red-400'
                      : style.dot === 'bg-amber-500'
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-muted-foreground',
                  )}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-sm font-semibold leading-snug">
                    {f.title ?? 'Untitled finding'}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                        style.badge,
                      )}
                    >
                      {style.label}
                    </span>
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
                      {statusLabel(f.status)}
                      {age ? ` · ${age}` : ''}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => acknowledge(f.id)}
                disabled={!online || busy}
                aria-label={`Mark acknowledged: ${f.title ?? 'finding'}`}
                className={cn(
                  'mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                  'border-primary/30 bg-primary/10 text-primary active:bg-primary/20',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Check className="h-4 w-4" aria-hidden="true" />
                )}
                Mark acknowledged
              </button>
            </section>
          );
        })}
      </div>
    </div>
  );
}
